import React, { useState } from "react";
import { Box, Button, Card, CircularProgress, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import DownloadIcon from "@mui/icons-material/Download";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PeopleIcon from "@mui/icons-material/People";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import FilePicker from "components/FilePicker";
import { useSnackbar } from "hooks/useSnackbar";
import { validateUserIds } from "services/splashScreenService";
import * as XLSX from "xlsx";

interface TargetedUsersUploadProps {
  value?: string[];
  onChange: (userIds: string[]) => void;
  disabled?: boolean;
}

const useStyles = makeStyles((theme) => ({
  card: {
    padding: "24px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    backgroundColor: "#ffffff",
    transition: "box-shadow 0.3s ease",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    },
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 20,
    "& svg": {
      fontSize: "1.5rem",
      color: theme.palette.primary.main,
    },
  },
  infoBox: {
    padding: "12px 16px",
    borderRadius: "8px",
    backgroundColor: "#e3f2fd",
    border: "1px solid #90caf9",
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
    "& svg": {
      color: "#1976d2",
    },
  },
  uploadSection: {
    display: "flex",
    flexFlow: "row wrap",
    gap: 16,
    alignItems: "flex-end",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  actions: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  uploadedInfo: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 16px",
    borderRadius: "8px",
    backgroundColor: "#e8f5e9",
    border: "1px solid #a5d6a7",
    marginTop: 16,
  },
}));

const TargetedUsersUpload = ({
  value = [],
  onChange,
  disabled = false,
}: TargetedUsersUploadProps) => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  const onSelectFile = async (selectedFile: File | null) => {
    if (!selectedFile) return;
    try {
      setLoading(true);

      const data = await selectedFile.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(firstSheet);

      const userIds = rows
        .map((row) => String(row["User ID"] || "").trim())
        .filter((id) => id.length > 0);

      if (userIds.length === 0) {
        setLoading(false);
        showSnackbar(
          "Invalid file format. Please ensure the file has a 'User ID' column.",
          "error",
        );
        return;
      }

      const uniqueUserIds = [...new Set(userIds)];

      const response = await validateUserIds(uniqueUserIds);

      if (!response?.status) {
        setLoading(false);
        showSnackbar(response?.message || "Error validating user IDs", "error");
        return;
      }

      const { validIds, invalidIds } = response.data;

      if (invalidIds && invalidIds.length > 0) {
        const displayIds =
          invalidIds.length > 5
            ? invalidIds.slice(0, 5).join(", ") +
              ` and ${invalidIds.length - 5} more`
            : invalidIds.join(", ");
        setLoading(false);
        showSnackbar(
          `The following User IDs are not found: ${displayIds}`,
          "error",
        );
        return;
      }

      onChange(validIds);
      setLoading(false);
      showSnackbar(
        `${validIds.length} targeted user(s) ready. Click Save to persist.`,
      );
    } catch (error) {
      setLoading(false);
      showSnackbar(
        error.message ||
          "Invalid file format. Please ensure the file has a 'User ID' column.",
        "error",
      );
    }
  };

  const onDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const rows = [{ "User ID": "60d5ec49f1a2c8b1f8e4e1a1" }];
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 30 }];
    XLSX.utils.book_append_sheet(wb, ws, "Target Users");

    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "targeted_users_template.xlsx";
    link.click();
    URL.revokeObjectURL(url);
  };

  const onRemoveUsers = () => {
    onChange([]);
  };

  return (
    <Card className={styles.card}>
      <Box className={styles.sectionHeader}>
        <PeopleIcon />
        <Typography variant="h6" fontWeight={600}>
          Upload Target Users
        </Typography>
      </Box>
      <Box className={styles.infoBox}>
        <InfoOutlinedIcon fontSize="small" />
        <Typography variant="body2">
          Select an Excel file (.xlsx, .xls) containing User IDs to target
          specific users. IDs are validated automatically on file selection —
          then click <strong>Save</strong> to persist. If no file is selected,
          the splash screen will be shown to all users.
        </Typography>
      </Box>
      <Box className={styles.uploadSection}>
        <Box flex={1}>
          <FilePicker
            label={
              loading
                ? "Validating..."
                : "Select Target Users File (.xlsx, .xls)"
            }
            name="targeted-users"
            fileType=".xlsx, .xls"
            value={null}
            showPreview={false}
            onFileSelect={onSelectFile}
            disabled={disabled || loading}
          />
          {loading && (
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Validating user IDs...
              </Typography>
            </Box>
          )}
        </Box>
        <Box className={styles.actions}>
          <Button
            variant="outlined"
            onClick={onDownloadTemplate}
            startIcon={<DownloadIcon />}
            size="large"
            disabled={loading}
          >
            Download Template
          </Button>
        </Box>
      </Box>
      {value && value.length > 0 && (
        <Box className={styles.uploadedInfo}>
          <PeopleIcon fontSize="small" color="success" />
          <Typography variant="body2">
            <strong>{value.length}</strong> targeted user(s) loaded
          </Typography>
          <IconButton size="small" onClick={onRemoveUsers}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Card>
  );
};

export default TargetedUsersUpload;
